class PremiumBannerSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-pbs-track]');
    this.slides = [...this.querySelectorAll('[data-pbs-slide]')];
    this.dots = [...this.querySelectorAll('[data-pbs-dot]')];
    this.previousButton = this.querySelector('[data-pbs-previous]');
    this.nextButton = this.querySelector('[data-pbs-next]');
    this.index = 0;
    this.pointerStart = null;

    if (!this.track || this.slides.length < 1) return;

    this.previousButton?.addEventListener('click', this.previous);
    this.nextButton?.addEventListener('click', this.next);
    this.dots.forEach((dot) => dot.addEventListener('click', this.handleDot));
    this.addEventListener('mouseenter', this.pause);
    this.addEventListener('mouseleave', this.start);
    this.addEventListener('focusin', this.pause);
    this.addEventListener('focusout', this.handleFocusOut);
    this.track.addEventListener('pointerdown', this.handlePointerDown);
    this.track.addEventListener('pointerup', this.handlePointerUp);
    this.track.addEventListener('pointercancel', this.clearPointer);
    this.track.addEventListener('click', this.handleSlideClick, true);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);

    this.render(false);
    this.start();
  }

  disconnectedCallback() {
    this.pause();
    this.previousButton?.removeEventListener('click', this.previous);
    this.nextButton?.removeEventListener('click', this.next);
    this.dots.forEach((dot) => dot.removeEventListener('click', this.handleDot));
    this.removeEventListener('mouseenter', this.pause);
    this.removeEventListener('mouseleave', this.start);
    this.removeEventListener('focusin', this.pause);
    this.removeEventListener('focusout', this.handleFocusOut);
    this.track?.removeEventListener('pointerdown', this.handlePointerDown);
    this.track?.removeEventListener('pointerup', this.handlePointerUp);
    this.track?.removeEventListener('pointercancel', this.clearPointer);
    this.track?.removeEventListener('click', this.handleSlideClick, true);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
  }

  previous = () => this.goTo(this.index - 1);
  next = () => this.goTo(this.index + 1);

  goTo(index, restart = true) {
    const shouldLoop = this.dataset.loop === 'true';
    if (shouldLoop) {
      this.index = (index + this.slides.length) % this.slides.length;
    } else {
      this.index = Math.max(0, Math.min(index, this.slides.length - 1));
    }
    this.render(true);
    if (restart) {
      this.pause();
      this.start();
    }
  }

  render(animate) {
    const mobileRatio = Number(this.slides[this.index]?.dataset.mobileRatio);
    if (mobileRatio > 0) this.style.setProperty('--pbs-mobile-ratio', String(mobileRatio));
    this.track.style.transitionDuration = animate ? '' : '0ms';
    this.track.style.transform = `translate3d(${-this.index * 100}%, 0, 0)`;

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.index;
      slide.setAttribute('aria-hidden', String(!active));
      slide.querySelectorAll('a, button').forEach((element) => {
        if (active) element.removeAttribute('tabindex');
        else element.setAttribute('tabindex', '-1');
      });
    });

    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === this.index;
      dot.setAttribute('aria-current', active ? 'true' : 'false');
      dot.setAttribute('aria-label', `${active ? 'Current slide' : 'Go to slide'} ${dotIndex + 1}`);
    });

    if (this.dataset.loop !== 'true') {
      if (this.previousButton) this.previousButton.disabled = this.index === 0;
      if (this.nextButton) this.nextButton.disabled = this.index === this.slides.length - 1;
    }

    requestAnimationFrame(() => {
      this.track.style.removeProperty('transition-duration');
    });
  }

  start = () => {
    if (this.dataset.autoplay !== 'true' || this.slides.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.pause();
    const interval = Number(this.dataset.interval || 5) * 1000;
    this.timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') this.next();
    }, interval);
  };

  pause = () => {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  };

  handleDot = (event) => {
    this.goTo(Number(event.currentTarget.dataset.pbsDot));
  };

  handleFocusOut = (event) => {
    if (!this.contains(event.relatedTarget)) this.start();
  };

  handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.pointerStart = { x: event.clientX, y: event.clientY };
    this.swiped = false;
    this.pause();
  };

  handlePointerUp = (event) => {
    if (this.pointerStart === null) return;
    const distance = event.clientX - this.pointerStart.x;
    const verticalDistance = event.clientY - this.pointerStart.y;
    this.pointerStart = null;
    if (Math.abs(distance) > 45 && Math.abs(distance) > Math.abs(verticalDistance) * 1.2) {
      this.swiped = true;
      distance < 0 ? this.next() : this.previous();
    } else {
      this.start();
    }
  };

  handleSlideClick = (event) => {
    if (!this.swiped) return;
    event.preventDefault();
    event.stopPropagation();
    this.swiped = false;
  };

  clearPointer = () => {
    this.pointerStart = null;
    this.start();
  };

  handleBlockSelect = (event) => {
    if (!this.contains(event.target)) return;
    const slideIndex = this.slides.indexOf(event.target.closest('[data-pbs-slide]'));
    if (slideIndex >= 0) {
      this.pause();
      this.goTo(slideIndex, false);
    }
  };
}

if (!customElements.get('premium-banner-slider')) {
  customElements.define('premium-banner-slider', PremiumBannerSlider);
}
